// The AI tools download as the folder the tools write locally (owner
// 2026-10-23: "just make it like his local telemetry is"), so the triage
// collector (caffeine-ai-harness Scripts~/triage/collect.py --root DIR, which
// reads DIR/sessions/*.jsonl and DIR/postmortems/*.json) runs on it unchanged:
//
//   telemetry/export.json                                what was exported (one JSON line)
//   telemetry/sessions/<yyyy-MM-dd>_<session>.jsonl      a session: its header line, then one line per record
//   telemetry/postmortems/<yyyy-MM-dd>_<HHmmss>_<session>.json   one postmortem
//
// The names are the tools' (ToolTelemetry.Append: the UTC day of the session's
// first record and its key; CaffeineFeedback: the postmortem's UTC time and its
// session's key). A session file is the export's `lines` byte for byte: the
// records as the server keeps them (value-equal to what was written; Postgres
// keeps no key order, and a record's local "owner" and "stamp" are never
// sent). A postmortem file is written the way the tool writes it: the body
// with the tool's key order put back (postmortemText) and two-space indented,
// with no trailing newline (Newtonsoft's Formatting.Indented, File.WriteAllText).

export const ROOT = 'telemetry/'
export const SESSIONS = `${ROOT}sessions/`
export const POSTMORTEMS = `${ROOT}postmortems/`
export const EXPORT_JSON = `${ROOT}export.json`

const pad = n => String(n).padStart(2, '0')

// A time's UTC day (yyyy-MM-dd) and time of day (HHmmss). A .NET round-trip
// string ("2026-10-19T15:41:27.9013360Z") is read as written; anything else
// through Date.
const utcParts = value => {
  const text = String(value || '')
  const exact = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d+)?Z$/.exec(text)
  if (exact) return { day: exact[1], time: `${exact[2]}${exact[3]}${exact[4]}` }
  const date = new Date(text)
  if (!Number.isFinite(date.getTime())) return { day: '0000-00-00', time: '000000' }
  return {
    day: `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`,
    time: `${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}`
  }
}

// A key as a file name part: the tools' keys are letters, digits and dashes.
const safeKey = key => String(key || 'unknown').replace(/[^A-Za-z0-9._-]/g, '-')

export const sessionFileName = item => `${utcParts(item.firstSeenTs).day}_${safeKey(item.session)}.jsonl`

export const postmortemFileName = (item, body) => {
  const { day, time } = utcParts(body && typeof body === 'object' && typeof body.ts === 'string' ? body.ts : item.ts)
  return `${day}_${time}_${safeKey(item.session)}.json`
}

// The tools' key order (MCPCaffeineTools CaffeineFeedback.SubmitPostmortem
// and Summarize, com.caffeine.mcp's postmortem, RecordStamp): a key the
// writer knows goes where the writer puts it; any other follows, in the
// order the server answered.
const ORDER = {
  '': ['kind', 'schema', 'ts', 'session', 'backend', 'install', 'title', 'summary', 'findings', 'keep', 'lessons', 'stats', 'id', 'source', 'owner', 'stamp', 'consent', 'scrubber', 'names'],
  'findings[]': ['title', 'severity', 'category', 'tool', 'action', 'whatHappened', 'workaround', 'proposal'],
  stats: ['calls', 'outcomes', 'responseBytes', 'largeResponses', 'retriesAfterError', 'busyRefusals', 'busyRefusalsNote', 'byTool', 'topErrors', 'largest', 'slowest', 'frictions', 'versions', 'startedAt'],
  'stats.byTool[]': ['call', 'count', 'failed', 'avgBytes', 'maxMs'],
  'stats.topErrors[]': ['error', 'count'],
  'stats.largest[]': ['call', 'bytes'],
  'stats.slowest[]': ['call', 'ms'],
  'stats.frictions[]': ['summary', 'tool', 'category', 'severity'],
  'stats.versions': ['backend', 'mcpVersion', 'unityVersion', 'platform', 'project', 'sdkVersion', 'toolsVersion', 'scrubber', 'names']
}

export const inToolOrder = (value, path = '') => {
  if (Array.isArray(value)) return value.map(item => inToolOrder(item, `${path}[]`))
  if (!value || typeof value !== 'object') return value
  const known = ORDER[path] || []
  const keys = [...known.filter(key => key in value), ...Object.keys(value).filter(key => !known.includes(key))]
  const out = {}
  keys.forEach(key => { out[key] = inToolOrder(value[key], path ? `${path}.${key}` : key) })
  return out
}

// A postmortem file's text. A body the server could not keep as a document (a
// string ai_tools_unlink() left in its place) is {"unlinked": <it>}, as the
// export does for a session's line, so the file is still one JSON object.
export const postmortemText = body => JSON.stringify(typeof body === 'string' ? { unlinked: body } : inToolOrder(body), null, 2)

// Names already taken get the next number on their day ("2026-10-19.2_<key>"),
// which keeps the session key the last part of the name.
const unique = (taken, name) => {
  if (!taken.has(name)) { taken.add(name); return name }
  const cut = name.indexOf('_')
  for (let n = 2; ; n++) {
    const next = `${name.slice(0, cut)}.${n}${name.slice(cut)}`
    if (!taken.has(next)) { taken.add(next); return next }
  }
}

// The zip's entries: the folders, export.json (`info`, one line), each session
// and each postmortem.
// A session the export cut at its per-session limit is named in export.json
// (truncatedFiles): its file can't say so itself.
export const folderEntries = ({ sessions = [], postmortems = [], info }) => {
  const taken = new Set()
  const files = []
  const truncatedFiles = []
  sessions.forEach(item => {
    const name = unique(taken, sessionFileName(item))
    if (item.truncated === true) truncatedFiles.push(`sessions/${name}`)
    files.push({ name: SESSIONS + name, data: String(item.lines || '') })
  })
  postmortems.forEach(item => files.push({ name: POSTMORTEMS + unique(taken, postmortemFileName(item, item.body)), data: postmortemText(item.body) }))
  const exported = { ...info, truncatedFiles }
  return [{ name: ROOT }, { name: EXPORT_JSON, data: `${JSON.stringify(exported)}\n` }, { name: SESSIONS }, { name: POSTMORTEMS }, ...files]
}
